import {
  QueryClient,
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  addAttachmentsToCellBoardMessage,
  addCellBoardMessageLink,
  calculateCellBoardMessageBulkCount,
  calculateCellBoardMessageCount,
  createCellBoardMessage,
  createCellBoardMessageBulk,
  deleteAttachmentFromCellBoardMessage,
  deleteCellBoardDefaultSafetyMessage,
  deleteCellBoardMessage,
  deleteCellBoardMessageLink,
  deleteCellBoardSafetyTopic,
  getCellBoardConfig,
  getCellBoardData,
  getCellBoardDefaultSafetyMessage,
  getCellBoardEnabledMCellInfos,
  getCellBoardMessage,
  getCellBoardMessages,
  getCellBoardMessagesGlobal,
  getCellBoardRosterInventoryCenterConfigs,
  getCellBoardSafetyMessages,
  getCellBoardSafetyTopic,
  getCellBoardWidgetData,
  getMCellMessageById,
  getMCellMessages,
  getProductionMetricIntervals,
  markMCellMessageRead,
  markMCellTaskComplete,
  markMCellTaskIncomplete,
  setCellBoardDefaultSafetyMessage,
  setCellBoardRosterInventoryCenterConfig,
  setCellBoardSafetyMessages,
  setCellBoardSafetyTopic,
  setNotesMCellTask,
  setProductionMetricIntervalAction,
  setProductionMetricIntervalNote,
  updateCellBoardConfig,
  updateCellBoardMessage,
  updateCellBoardMessageLink,
  updateCellBoardMessageTasks,
  upsertCellBoardWidgetData,
} from './api';
import {
  CellBoardData,
  CellBoardMessageBulkCreate,
  CellBoardMessageBulkCountRequest,
  CellBoardMessageFilter,
  CellBoardConfig,
  CellBoardRosterInventoryCenterConfig,
  MCellMessageCountResult,
  ProductionMetricIntervalDto,
  ProductionMetricIntervalFilter,
  CellBoardMessageInfo,
} from './types';
import { Shift } from '../resourceTracking/types';
import { useNavigation } from '../../utils/navigation';
import { QueryKeys } from '../../api/queryKeys';
import { useAlert } from '../common/AlertContext';
import { EMPTY_GUID } from '../../utils/string-utils';
import { DATA_REFRESH_INTERVAL } from '../../utils/date';
import { useInvalidatingMutation } from '../../api/useInvalidatingMutation';

/**
 * Custom React Query Hook for fetching the message count for a cell board.
 *
 * @param {CellBoardMessageInfo} messageInfo - The information required to calculate the message count.
 * @param {boolean} [enabled=true] - A boolean to control if the query should be enabled.
 *
 * @returns {UseQueryResult<MCellMessageCountResult>} - The result object from the 'useQuery' hook of React Query. This object contains several fields including:
 * - `data`: The actual data received from the query.
 * - `status`: The status of the query ('idle', 'loading', 'error', or 'success').
 * - `error`: Any error message that occurred during the query.
 * - `isFetching`: A boolean that indicates whether a fetch is ongoing.
 * - `isLoading`: A boolean that indicates whether the initial fetch is still ongoing.
 * - `isError`: A boolean that indicates whether the fetch failed.
 * - `isSuccess`: A boolean that indicates whether the fetch was successful.
 * - `refetch`: A function to manually refetch the data.
 */
export const useCellBoardMessageCountQuery = (
  messageInfo: CellBoardMessageInfo,
  enabled: boolean = true, // This controls if the query should be enabled
): UseQueryResult<MCellMessageCountResult> => {
  return useQuery<MCellMessageCountResult>({
    queryKey: [QueryKeys.CELL_BOARD_MESSAGE_COUNT, messageInfo],
    queryFn: () => calculateCellBoardMessageCount(messageInfo),
    enabled,
  });
};

/**
 * Custom React Query Hook for fetching the message count for a cell board.
 *
 * @param {CellBoardMessageBulkCountRequest} request - The information required to calculate the message count.
 * @param {boolean} [enabled=true] - A boolean to control if the query should be enabled.
 *
 * @returns {UseQueryResult<MCellMessageCountResult>} - The result object from the 'useQuery' hook of React Query. This object contains several fields including:
 * - `data`: The actual data received from the query.
 * - `status`: The status of the query ('idle', 'loading', 'error', or 'success').
 * - `error`: Any error message that occurred during the query.
 * - `isFetching`: A boolean that indicates whether a fetch is ongoing.
 * - `isLoading`: A boolean that indicates whether the initial fetch is still ongoing.
 * - `isError`: A boolean that indicates whether the fetch failed.
 * - `isSuccess`: A boolean that indicates whether the fetch was successful.
 * - `refetch`: A function to manually refetch the data.
 */
export const useCellBoardMessageBulkCountQuery = (
  request: CellBoardMessageBulkCountRequest,
  enabled: boolean = true,
): UseQueryResult<MCellMessageCountResult> => {
  return useQuery<MCellMessageCountResult>({
    queryKey: [QueryKeys.CELL_BOARD_MESSAGE_COUNT_BULK, request],
    queryFn: () => calculateCellBoardMessageBulkCount(request),
    enabled,
  });
};

/**
 * Fetches the Production Metric Intervals (Hour by Hour) matching a filter.
 * @param filter
 * @param refetchInterval
 */
export const useCellBoardProductionMetricIntervalsQuery = (
  filter: ProductionMetricIntervalFilter,
  refetchInterval?: number | false,
): UseQueryResult<ProductionMetricIntervalDto[]> =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_PRODUCTION_METRIC_INTERVALS, filter],
    queryFn: () => getProductionMetricIntervals(filter),
    refetchInterval,
    meta: { errorMessageKey: 'errorFetchingProductionMetricIntervals' },
  });

/**
 * Fetches all Cell Board Data (config included) for an MCell and Shift.
 * @param mCellId
 * @param shift
 * @param enabled
 * @param poll - Refetch every DATA_REFRESH_INTERVAL, unless the cell uses a custom Cell Dashboard
 * Template, whose widgets fetch their own data.
 */
export const useCellBoardDataQuery = (
  mCellId: string,
  shift: Shift,
  enabled: boolean = true,
  poll: boolean = false,
): UseQueryResult<CellBoardData> =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_DATA, mCellId, shift],
    queryFn: () => getCellBoardData({ mCellId, shift }),
    enabled: enabled && mCellId !== EMPTY_GUID,
    // As fresh as the poll, so the pages and quadrants sharing this heavy query don't refetch it on mount
    staleTime: DATA_REFRESH_INTERVAL,
    refetchInterval: (query) => {
      const templateId = query.state.data?.config?.cellDashboardTemplateId;
      const hasTemplate = !!templateId && templateId !== EMPTY_GUID;
      return poll && !hasTemplate ? DATA_REFRESH_INTERVAL : false;
    },
    meta: { errorMessageKey: 'errorFetchingCellBoardMetrics' },
  });

/**
 * Refetches Cell Board Message lists and details (after a mutation or a SignalR push).
 */
export const invalidateCellBoardMessages = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: [QueryKeys.CELL_BOARD_MESSAGES] });

/**
 * Refetches MCell Message lists and details (after a mutation or a SignalR push).
 */
export const invalidateMCellMessages = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ queryKey: [QueryKeys.MCELL_MESSAGES] });

/**
 * Creates one or more Cell Board Messages, then shows a success alert and navigates to the message list.
 */
export const useCellBoardMessageCreation = () => {
  const queryClient = useQueryClient();
  const { navigateToCellBoardMessage } = useNavigation();
  const { setAlert } = useAlert();

  const createBulkMessages = useMutation({
    mutationFn: async (data: CellBoardMessageBulkCreate) =>
      data.cellBoardMessages.length === 1
        ? [await createCellBoardMessage(data.cellBoardMessages[0])]
        : await createCellBoardMessageBulk(data),
    onSuccess: (createdMessages) => {
      invalidateCellBoardMessages(queryClient);
      // alert and navigate only when messages were created
      if (createdMessages.length > 0) {
        setAlert(
          { stringKey: 'cellBoardMessageCreatedSuccessfully', props: {} },
          'success',
        );
        navigateToCellBoardMessage();
      }
    },
    onError: (err) => {
      setAlert(
        {
          stringKey: 'errorCreatingCellBoardMessage',
          props: { error: err.message || 'Unknown error' },
        },
        'error',
      );
    },
  });

  return {
    createBulkMessages,
    isCreating: createBulkMessages.isPending,
  };
};

/**
 * Fetches the current user's Cell Board Messages for a location, or every user's when `global`.
 * @param filter
 * @param global
 */
export const useCellBoardMessagesQuery = (
  filter: CellBoardMessageFilter,
  global: boolean,
) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_MESSAGES, 'list', filter, global],
    queryFn: () =>
      global
        ? getCellBoardMessagesGlobal(filter)
        : getCellBoardMessages(filter),
    refetchInterval: DATA_REFRESH_INTERVAL,
  });

/**
 * Fetches a Cell Board Message by id.
 * @param id
 */
export const useCellBoardMessageQuery = (id: string | undefined) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_MESSAGES, 'detail', id],
    queryFn: () => getCellBoardMessage(id!),
    enabled: !!id,
  });

const CELL_BOARD_MESSAGES_KEY = [QueryKeys.CELL_BOARD_MESSAGES];

/**
 * Updates all Cell Board Message fields except attachments and tasks.
 */
export const useUpdateCellBoardMessageMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    updateCellBoardMessage,
    'errorUpdatingCellBoardMessage',
  );

/**
 * Replaces the tasks of a Cell Board Message.
 */
export const useUpdateCellBoardMessageTasksMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    updateCellBoardMessageTasks,
    'errorUpdatingCellBoardMessageTasks',
  );

/**
 * Updates a Cell Board Message link.
 */
export const useUpdateCellBoardMessageLinkMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    updateCellBoardMessageLink,
    'errorUpdatingCellBoardMessageLink',
  );

/**
 * Adds a link to a Cell Board Message.
 */
export const useAddCellBoardMessageLinkMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    addCellBoardMessageLink,
    'errorAddingCellBoardMessageLink',
  );

/**
 * Deletes a Cell Board Message link.
 */
export const useDeleteCellBoardMessageLinkMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    deleteCellBoardMessageLink,
    'errorDeletingCellBoardMessageLink',
  );

/**
 * Adds attachments to a Cell Board Message.
 */
export const useAddCellBoardMessageAttachmentsMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    addAttachmentsToCellBoardMessage,
    'errorAddingAttachmentsToCellBoardMessage',
  );

/**
 * Deletes an attachment from a Cell Board Message.
 */
export const useDeleteCellBoardMessageAttachmentMutation = () =>
  useInvalidatingMutation(
    CELL_BOARD_MESSAGES_KEY,
    deleteAttachmentFromCellBoardMessage,
    'errorDeletingAttachmentFromCellBoardMessage',
  );

/**
 * Deletes a Cell Board Message. Refetches the lists only, as the deleted message's detail would 404.
 */
export const useDeleteCellBoardMessageMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.CELL_BOARD_MESSAGES, 'list'],
    deleteCellBoardMessage,
    'errorDeletingCellBoardMessage',
  );

/**
 * Fetches the active MCell Messages for an MCell and Shift, refetching every DATA_REFRESH_INTERVAL.
 * @param mCellId
 * @param shift
 * @param enabled
 */
export const useMCellMessagesQuery = (
  mCellId: string,
  shift: Shift,
  enabled: boolean = true,
) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_MESSAGES, 'list', mCellId, shift],
    queryFn: () => getMCellMessages({ mCellId, shift }),
    enabled: enabled && mCellId !== EMPTY_GUID,
    refetchInterval: DATA_REFRESH_INTERVAL,
    meta: { errorMessageKey: 'errorFetchingWorkCellMessages' },
  });

/**
 * Fetches an MCell Message by id.
 * @param id
 */
export const useMCellMessageQuery = (id: string | undefined) =>
  useQuery({
    queryKey: [QueryKeys.MCELL_MESSAGES, 'detail', id],
    queryFn: () => getMCellMessageById(id!),
    enabled: !!id,
    meta: { errorMessageKey: 'errorFetchingWorkCellMessage' },
  });

const MCELL_MESSAGES_KEY = [QueryKeys.MCELL_MESSAGES];

/**
 * Marks an MCell Message as read.
 */
export const useMarkMCellMessageReadMutation = () =>
  useInvalidatingMutation(
    MCELL_MESSAGES_KEY,
    markMCellMessageRead,
    'errorMarkingMCellMessageRead',
  );

/**
 * Marks an MCell Task as complete.
 */
export const useMarkMCellTaskCompleteMutation = () =>
  useInvalidatingMutation(
    MCELL_MESSAGES_KEY,
    markMCellTaskComplete,
    'errorMarkingMCellTaskComplete',
  );

/**
 * Marks an MCell Task as incomplete.
 */
export const useMarkMCellTaskIncompleteMutation = () =>
  useInvalidatingMutation(
    MCELL_MESSAGES_KEY,
    markMCellTaskIncomplete,
    'errorMarkingMCellTaskIncomplete',
  );

/**
 * Sets the notes on an MCell Task.
 */
export const useSetMCellTaskNotesMutation = () =>
  useInvalidatingMutation(
    MCELL_MESSAGES_KEY,
    setNotesMCellTask,
    'errorSettingCellBoardTaskNotes',
  );

/**
 * Fetches the Cell Board Config for an MCell and Shift ('Active' resolves to the current shift).
 * @param mCellId
 * @param shift
 */
export const useCellBoardConfigQuery = (mCellId: string, shift: Shift) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_CONFIG, mCellId, shift],
    queryFn: () => getCellBoardConfig({ mCellId, shift }),
    enabled: mCellId !== EMPTY_GUID,
    meta: { errorMessageKey: 'errorFetchingCellBoardSettings' },
  });

/**
 * Cell Board admin mutation. Also refetches Cell Board Data, which embeds the config, widget data,
 * safety message and topic, and roster, so the board shows the change when the user returns to it.
 */
const useCellBoardAdminMutation = <TVariables, TData, TItem = never>(
  queryKey: QueryKey,
  mutationFn: (variables: TVariables) => Promise<TData>,
  errorMessageKey: string,
  optimisticUpdate?: (list: TItem[], variables: TVariables) => TItem[],
) =>
  useInvalidatingMutation(
    queryKey,
    mutationFn,
    errorMessageKey,
    optimisticUpdate,
    [[QueryKeys.CELL_BOARD_DATA]],
  );

/**
 * Updates a Cell Board Config.
 */
export const useUpdateCellBoardConfigMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_CONFIG],
    updateCellBoardConfig,
    'errorUpdatingCellBoardSettings',
  );

/**
 * Fetches the widget data (e.g. embed URLs) of a Cell Board Config. Keyed by the config's templates
 * too, so it refetches when they change.
 * @param config
 */
export const useCellBoardWidgetDataQuery = (
  config: CellBoardConfig | undefined,
) =>
  useQuery({
    queryKey: [
      QueryKeys.CELL_BOARD_WIDGET_DATA,
      config?.id,
      config?.cellDashboardTemplateId,
      config?.scorecardTemplateId,
    ],
    queryFn: () => getCellBoardWidgetData(config!.id),
    enabled: !!config?.id,
  });

/**
 * Saves the widget data of a Cell Board Config and caches the result. Errors are left to the caller,
 * which shows them inline.
 */
export const useUpsertCellBoardWidgetDataMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: upsertCellBoardWidgetData,
    onSuccess: (widgetData, { id }) => {
      queryClient.setQueriesData(
        { queryKey: [QueryKeys.CELL_BOARD_WIDGET_DATA, id] },
        widgetData,
      );
      return queryClient.invalidateQueries({
        queryKey: [QueryKeys.CELL_BOARD_DATA],
      });
    },
  });
};

type CellBoardSafetyMessagesFilter = Parameters<
  typeof getCellBoardSafetyMessages
>[0];

/**
 * Fetches the Cell Board Safety Messages of a location for several shifts.
 * @param filter
 */
export const useCellBoardSafetyMessagesBulkQuery = (
  filter: CellBoardSafetyMessagesFilter,
) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_SAFETY_MESSAGES_BULK, filter],
    queryFn: () => getCellBoardSafetyMessages(filter),
    meta: { errorMessageKey: 'errorFetchingCellBoardSafetyMessages' },
  });

/**
 * Sets the Cell Board Safety Message of a location for several shifts.
 */
export const useSetCellBoardSafetyMessagesMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_SAFETY_MESSAGES_BULK],
    setCellBoardSafetyMessages,
    'errorSettingCellBoardSafetyMessages',
  );

/**
 * Fetches the Default Safety Message of an Inventory Center (null when it has none).
 * @param inventoryCenterId
 */
export const useCellBoardDefaultSafetyMessageQuery = (
  inventoryCenterId: string,
) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_DEFAULT_SAFETY_MESSAGE, inventoryCenterId],
    // An Inventory Center without one returns 204, which axios reads as ''
    queryFn: async () =>
      (await getCellBoardDefaultSafetyMessage(inventoryCenterId)) || null,
    enabled: inventoryCenterId !== EMPTY_GUID,
    meta: { errorMessageKey: 'errorFetchingCellBoardDefaultSafetyMessage' },
  });

/**
 * Sets the Default Safety Message of an Inventory Center.
 */
export const useSetCellBoardDefaultSafetyMessageMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_DEFAULT_SAFETY_MESSAGE],
    setCellBoardDefaultSafetyMessage,
    'errorSettingCellBoardDefaultSafetyMessage',
  );

/**
 * Deletes a Default Safety Message.
 */
export const useDeleteCellBoardDefaultSafetyMessageMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_DEFAULT_SAFETY_MESSAGE],
    deleteCellBoardDefaultSafetyMessage,
    'errorDeletingCellBoardDefaultSafetyMessage',
  );

/**
 * Fetches the Safety Topic of an Inventory Center (null when it has none).
 * @param inventoryCenterId
 */
export const useCellBoardSafetyTopicQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_SAFETY_TOPIC, inventoryCenterId],
    // An Inventory Center without one returns 204, which axios reads as ''
    queryFn: async () =>
      (await getCellBoardSafetyTopic(inventoryCenterId)) || null,
    enabled: inventoryCenterId !== EMPTY_GUID,
    meta: { errorMessageKey: 'errorFetchingCellBoardSafetyTopic' },
  });

/**
 * Uploads the Safety Topic file of an Inventory Center.
 */
export const useSetCellBoardSafetyTopicMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_SAFETY_TOPIC],
    setCellBoardSafetyTopic,
    'errorSettingCellBoardSafetyTopic',
  );

/**
 * Deletes a Safety Topic.
 */
export const useDeleteCellBoardSafetyTopicMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_SAFETY_TOPIC],
    deleteCellBoardSafetyTopic,
    'errorDeletingCellBoardSafetyTopic',
  );

/**
 * Fetches which MCells of an Inventory Center have Cell Board enabled.
 * @param inventoryCenterId
 */
export const useCellBoardEnabledMCellInfosQuery = (inventoryCenterId: string) =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_ENABLED_MCELL_INFOS, inventoryCenterId],
    queryFn: () => getCellBoardEnabledMCellInfos(inventoryCenterId),
    meta: { errorMessageKey: 'errorFetchingEnabledWorkCells' },
  });

/**
 * Fetches the Cell Board Roster config of every Inventory Center.
 */
export const useCellBoardRosterInventoryCenterConfigsQuery = () =>
  useQuery({
    queryKey: [QueryKeys.CELL_BOARD_ROSTER_INVENTORY_CENTER_CONFIGS],
    queryFn: getCellBoardRosterInventoryCenterConfigs,
    meta: { errorMessageKey: 'errorFetchingCellBoardRosterConfigurations' },
  });

/**
 * Sets the Cell Board Roster config of an Inventory Center, showing it right away.
 */
export const useSetCellBoardRosterInventoryCenterConfigMutation = () =>
  useCellBoardAdminMutation(
    [QueryKeys.CELL_BOARD_ROSTER_INVENTORY_CENTER_CONFIGS],
    setCellBoardRosterInventoryCenterConfig,
    'errorSettingCellBoardRosterConfiguration',
    (configs: CellBoardRosterInventoryCenterConfig[], config) => [
      ...configs.filter(
        (c) => c.inventoryCenterId !== config.inventoryCenterId,
      ),
      config,
    ],
  );

/**
 * Sets the note of a Production Metric Interval.
 */
export const useSetProductionMetricIntervalNoteMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.CELL_BOARD_PRODUCTION_METRIC_INTERVALS],
    setProductionMetricIntervalNote,
    'errorSettingProductionMetricIntervalNote',
  );

/**
 * Sets the action of a Production Metric Interval.
 */
export const useSetProductionMetricIntervalActionMutation = () =>
  useInvalidatingMutation(
    [QueryKeys.CELL_BOARD_PRODUCTION_METRIC_INTERVALS],
    setProductionMetricIntervalAction,
    'errorSettingProductionMetricIntervalAction',
  );
